import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  Users,
  Briefcase,
  FileText,
  Building2,
  UserCheck,
  ShieldCheck,
  MapPin,
  TrendingUp,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Search,
} from "lucide-react";
import Header from "../component/Header";

const API_URL = process.env.REACT_APP_API_URL; // e.g. "https://api.example.com/api/"

const NIGERIA_STATES = [
  "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa", "Benue",
  "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti", "Enugu",
  "FCT", "Gombe", "Imo", "Jigawa", "Kaduna", "Kano", "Katsina", "Kebbi",
  "Kogi", "Kwara", "Lagos", "Nasarawa", "Niger", "Ogun", "Ondo", "Osun",
  "Oyo", "Plateau", "Rivers", "Sokoto", "Taraba", "Yobe", "Zamfara",
];

const WORK_TYPES = ["Full-time", "Part-time", "Contract", "Internship", "Remote"];

const CHART_COLORS = ["#2563eb", "#f97316", "#10b981", "#8b5cf6", "#f43f5e", "#06b6d4", "#eab308"];

const insightStyles = {
  location: "bg-blue-600 text-white",
  trend: "bg-white border border-orange-200 text-gray-700",
  gap: "bg-white border border-red-200 text-gray-700",
};
const insightIcon = { location: MapPin, trend: TrendingUp, gap: AlertTriangle };

// ── small helpers ───────────────────────────────────────────────────────────
const numberFmt = (n) => (typeof n === "number" ? n.toLocaleString() : n ?? "—");

const SummaryCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col items-center text-center gap-2">
    <Icon size={18} className={accent} />
    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
      {label}
    </span>
    <span className={`text-2xl font-bold ${accent}`}>{value}</span>
  </div>
);

const Panel = ({ title, children, className = "" }) => (
  <div className={`bg-white border border-gray-200 rounded-xl p-5 ${className}`}>
    {title && <h3 className="text-sm font-semibold text-gray-800 mb-4">{title}</h3>}
    {children}
  </div>
);

const EmptyNote = ({ children }) => (
  <p className="text-xs text-gray-400 italic py-6 text-center">{children}</p>
);

// ── main component ──────────────────────────────────────────────────────────
export default function GeneralStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // filter draft state (only applied to the fetch on "Apply")
  const [draftFilters, setDraftFilters] = useState({
    state: "",
    industry: "",
    workType: "",
    period: "",
  });
  const [activeFilters, setActiveFilters] = useState({
    state: "",
    industry: "",
    workType: "",
    period: "",
  });

  const [explorerSearch, setExplorerSearch] = useState("");
  const [explorerSort, setExplorerSort] = useState({ key: "jobs", dir: "desc" });
  const [expandedStateRows, setExpandedStateRows] = useState({});
  const [stateRowDetail, setStateRowDetail] = useState({});
  const [showAllStates, setShowAllStates] = useState(false);

  const fetchStats = useCallback(async (filters) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams(
        Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
      );
      const res = await fetch(`${API_URL}stat?${params.toString()}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load statistics.");
      const data = await res.json();
      setStats(data);
    } catch (err) {
      setError(err.message || "Something went wrong loading statistics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats(activeFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = () => {
    setActiveFilters(draftFilters);
    fetchStats(draftFilters);
  };

  const handleClearFilters = () => {
    const cleared = { state: "", industry: "", workType: "", period: "" };
    setDraftFilters(cleared);
    setActiveFilters(cleared);
    fetchStats(cleared);
  };

  const industryOptions = useMemo(
    () => (stats?.jobsByIndustry || []).map((i) => i.industry).filter(Boolean),
    [stats]
  );

  const toggleStateRow = async (stateName) => {
    setExpandedStateRows((prev) => ({ ...prev, [stateName]: !prev[stateName] }));
    if (!stateRowDetail[stateName]) {
      try {
        const res = await fetch(`${API_URL}stats/state/${encodeURIComponent(stateName)}`, {
          credentials: "include",
        });
        if (res.ok) {
          const detail = await res.json();
          setStateRowDetail((prev) => ({ ...prev, [stateName]: detail }));
        }
      } catch {
        /* silently ignore — row just won't expand with detail */
      }
    }
  };

  const filteredExplorerRows = useMemo(() => {
    if (!stats?.topJobTitles) return [];
    let rows = stats.topJobTitles;
    if (explorerSearch.trim()) {
      const q = explorerSearch.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.jobTitle?.toLowerCase().includes(q) ||
          r.industry?.toLowerCase().includes(q) ||
          r.state?.toLowerCase().includes(q)
      );
    }
    const { key, dir } = explorerSort;
    rows = [...rows].sort((a, b) => {
      const av = a[key] ?? 0;
      const bv = b[key] ?? 0;
      if (typeof av === "string") return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return dir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [stats, explorerSearch, explorerSort]);

  const toggleSort = (key) =>
    setExplorerSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }
    );

  if (loading && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-500 text-sm">Loading employment statistics…</p>
        </div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <p className="text-gray-600 text-sm">{error}</p>
      </div>
    );
  }

  const s = stats?.summary || {};
  const maxStateCount = Math.max(...(stats?.jobseekersByState || []).map((x) => x.count), 1);
  const visibleStates = showAllStates
    ? stats?.jobseekersByState || []
    : (stats?.jobseekersByState || []).slice(0, 5);

  return (
    <>
    <Header/>
      <div className="min-h-screen mt-20  bg-slate-50 pb-16">
      {/* ── page header ── */}
      <div className="text-center pt-10 pb-6 px-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-blue-700">
          Nigeria Employment &amp; Jobseeker Statistics
        </h1>
        <p className="text-gray-500 text-sm mt-2 max-w-2xl mx-auto">
          Explore comprehensive demographic, trends, and real-time insights into the Nigeria
          labour market for jobseekers and employers.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 space-y-6">
        {/* ── summary cards ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <SummaryCard icon={Users} label="Total Jobseekers" value={numberFmt(s.totalJobseekers)} accent="text-blue-600" />
          <SummaryCard icon={Briefcase} label="Active Jobs" value={numberFmt(s.activeJobs)} accent="text-orange-500" />
          <SummaryCard icon={FileText} label="Applications" value={numberFmt(s.totalApplications)} accent="text-violet-600" />
          <SummaryCard icon={Building2} label="Registered Employers" value={numberFmt(s.registeredEmployers)} accent="text-slate-600" />
          <SummaryCard icon={UserCheck} label="Employed" value={numberFmt(s.employedCount)} accent="text-emerald-600" />
          <SummaryCard icon={ShieldCheck} label="NIN Verified" value={numberFmt(s.ninVerifiedCount)} accent="text-cyan-600" />
        </div>

        {/* ── filter bar ── */}
        <Panel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">State</label>
              <select
                value={draftFilters.state}
                onChange={(e) => setDraftFilters((p) => ({ ...p, state: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">All States</option>
                {NIGERIA_STATES.map((st) => (
                  <option key={st} value={st}>{st}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Industry</label>
              <select
                value={draftFilters.industry}
                onChange={(e) => setDraftFilters((p) => ({ ...p, industry: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">All Industries</option>
                {industryOptions.map((ind) => (
                  <option key={ind} value={ind}>{ind}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Job Type</label>
              <select
                value={draftFilters.workType}
                onChange={(e) => setDraftFilters((p) => ({ ...p, workType: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">All Types</option>
                {WORK_TYPES.map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Time Period</label>
              <input
                type="month"
                value={draftFilters.period}
                onChange={(e) => setDraftFilters((p) => ({ ...p, period: e.target.value }))}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleApplyFilters}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg py-2 transition"
              >
                Apply
              </button>
              <button
                onClick={handleClearFilters}
                className="text-sm text-gray-400 hover:text-gray-600 px-2"
              >
                Clear
              </button>
            </div>
          </div>
        </Panel>

        {/* ── results line ── */}
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          Results: {stats?.appliedFilters?.state} | {stats?.appliedFilters?.industry} |{" "}
          {stats?.appliedFilters?.workType} | {stats?.appliedFilters?.period}
        </p>

        {/* ── jobseeker demographics ── */}
        <div>
          <h2 className="text-base font-semibold text-gray-800 mb-3">Jobseeker Demographics</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Gender &amp; Age">
              <div className="grid grid-cols-2 gap-4">
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats?.demographics?.gender || []}
                        dataKey="count"
                        nameKey="label"
                        innerRadius={40}
                        outerRadius={65}
                        paddingAngle={2}
                      >
                        {(stats?.demographics?.gender || []).map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats?.demographics?.age || []} layout="vertical" margin={{ left: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" hide />
                      <YAxis dataKey="label" type="category" width={70} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Panel>

            <Panel title="Marital Status &amp; Education">
              <div className="grid grid-cols-2 gap-4">
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats?.demographics?.marital || []}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#f97316" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats?.demographics?.education || []}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Panel>
          </div>
        </div>

        {/* ── jobseekers by state ── */}
        <div>
          <h2 className="text-base font-semibold text-gray-800 mb-3">Jobseekers by State</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="All States (heatmap)">
              {stats?.jobseekersByState?.length ? (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {stats.jobseekersByState.map((row) => {
                    const intensity = row.count / maxStateCount;
                    return (
                      <button
                        key={row.state}
                        onClick={() =>
                          setDraftFilters((p) => ({ ...p, state: row.state })) ||
                          setActiveFilters((p) => ({ ...p, state: row.state })) ||
                          fetchStats({ ...activeFilters, state: row.state })
                        }
                        title={`${row.state}: ${row.count} jobseekers`}
                        style={{ backgroundColor: `rgba(37, 99, 235, ${0.15 + intensity * 0.75})` }}
                        className="rounded-lg p-2 text-center transition hover:scale-105"
                      >
                        <span
                          className={`block text-[10px] font-semibold truncate ${
                            intensity > 0.5 ? "text-white" : "text-blue-900"
                          }`}
                        >
                          {row.state}
                        </span>
                        <span
                          className={`block text-xs font-bold ${
                            intensity > 0.5 ? "text-white" : "text-blue-900"
                          }`}
                        >
                          {row.count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <EmptyNote>No state data available yet.</EmptyNote>
              )}
            </Panel>

            <Panel title="Top States">
              <div className="space-y-2">
                {visibleStates.map((row, i) => (
                  <div
                    key={row.state}
                    className="flex items-center justify-between p-2.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
                    onClick={() => fetchStats({ ...activeFilters, state: row.state })}
                  >
                    <span className="text-sm text-gray-700">
                      {i + 1}. {row.state}
                    </span>
                    <span className="text-sm font-bold text-blue-600">{row.count}</span>
                  </div>
                ))}
              </div>
              {stats?.jobseekersByState?.length > 5 && (
                <button
                  onClick={() => setShowAllStates((v) => !v)}
                  className="text-xs text-blue-600 font-medium mt-3 hover:underline"
                >
                  {showAllStates
                    ? "Show fewer states"
                    : `...and ${stats.jobseekersByState.length - 5} more states`}
                </button>
              )}
            </Panel>
          </div>
        </div>

        {/* ── state spotlight ── */}
        {stats?.stateSpotlight && (
          <div>
            <div className="flex items-center gap-1.5 mb-3">
              <span className="text-amber-400">★</span>
              <h2 className="text-base font-semibold text-gray-800">
                {stats.stateSpotlight.state} Spotlight
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Panel>
                <span className="text-xs font-semibold text-gray-400 uppercase">Total Subscribers</span>
                <div className="text-4xl font-bold text-blue-600 mt-2">
                  {numberFmt(stats.stateSpotlight.totalSubscribers)}
                </div>
                <div className="flex gap-4 mt-4 text-xs text-gray-500">
                  <span>{numberFmt(stats.stateSpotlight.jobsInState)} jobs</span>
                  <span>{numberFmt(stats.stateSpotlight.applicationsInState)} applications</span>
                </div>
              </Panel>

              <Panel title="Gender Breakdown" className="lg:col-span-1">
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.stateSpotlight.genderBreakdown}
                        dataKey="count"
                        nameKey="label"
                        outerRadius={60}
                      >
                        {stats.stateSpotlight.genderBreakdown.map((_, i) => (
                          <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel title="Top LGAs">
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {stats.stateSpotlight.lgaBreakdown.slice(0, 8).map((lga) => (
                    <div key={lga.lga} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{lga.lga}</span>
                      <span className="font-semibold text-gray-800">{lga.count}</span>
                    </div>
                  ))}
                  {!stats.stateSpotlight.lgaBreakdown.length && (
                    <EmptyNote>No LGA data recorded for this state.</EmptyNote>
                  )}
                </div>
              </Panel>
            </div>
          </div>
        )}

        {/* ── jobs by industry + funnel ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Jobs by Industry">
            {stats?.jobsByIndustry?.length ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.jobsByIndustry} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="industry" type="category" width={90} tick={{ fontSize: 11 }} />
                    <Tooltip
                      formatter={(value, name, props) => [
                        `${value} jobs (${props.payload.applications} applications)`,
                        "",
                      ]}
                    />
                    <Bar dataKey="jobs" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyNote>No jobs match the current filters.</EmptyNote>
            )}
          </Panel>

          <Panel title="Employment Funnel">
            <div className="space-y-2">
              {(stats?.employmentFunnel || []).map((stage, i) => {
                const max = stats.employmentFunnel[0]?.count || 1;
                const pct = Math.max((stage.count / max) * 100, 4);
                return (
                  <div key={stage.stage} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-24 flex-shrink-0">{stage.stage}</span>
                    <div className="flex-1 bg-gray-100 rounded-md h-8 overflow-hidden">
                      <div
                        className="h-full rounded-md flex items-center justify-end pr-2 text-white text-xs font-semibold"
                        style={{
                          width: `${pct}%`,
                          backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                        }}
                      >
                        {stage.count}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Panel>
        </div>

        {/* ── data explorer ── */}
        <div>
          <h2 className="text-base font-semibold text-gray-800 mb-3">Data Explorer</h2>
          <Panel>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <span className="text-sm font-semibold text-gray-700">Top Job Titles</span>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                <input
                  value={explorerSearch}
                  onChange={(e) => setExplorerSearch(e.target.value)}
                  placeholder="Search title, industry, state…"
                  className="text-sm border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-200 w-64"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-400 uppercase border-b border-gray-100">
                    {[
                      ["jobTitle", "Job Title"],
                      ["industry", "Industry"],
                      ["state", "State"],
                      ["jobs", "Jobs"],
                      ["applications", "Applications"],
                    ].map(([key, label]) => (
                      <th
                        key={key}
                        onClick={() => toggleSort(key)}
                        className="py-2 pr-4 cursor-pointer select-none hover:text-gray-600"
                      >
                        {label} {explorerSort.key === key && (explorerSort.dir === "asc" ? "↑" : "↓")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredExplorerRows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="py-2 pr-4 text-gray-800">{row.jobTitle}</td>
                      <td className="py-2 pr-4 text-gray-500">{row.industry}</td>
                      <td className="py-2 pr-4 text-gray-500">{row.state}</td>
                      <td className="py-2 pr-4 font-semibold text-blue-600">{row.jobs}</td>
                      <td className="py-2 pr-4 text-gray-500">{row.applications}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!filteredExplorerRows.length && (
                <EmptyNote>No job titles match your search.</EmptyNote>
              )}
            </div>
          </Panel>
        </div>

        {/* ── jobs & applications by state (all states) ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Jobs &amp; Applications by State">
            <div className="max-h-72 overflow-y-auto pr-1 space-y-1.5">
              {(stats?.activityByState || []).map((row) => (
                <div
                  key={row.state}
                  className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0"
                >
                  <span className="text-gray-700">{row.state}</span>
                  <span className="text-gray-500">
                    <span className="font-semibold text-blue-600">{row.jobs}</span> jobs ·{" "}
                    <span className="font-semibold text-violet-600">{row.applications}</span> apps
                  </span>
                </div>
              ))}
              {!stats?.activityByState?.length && (
                <EmptyNote>No job/application activity recorded yet.</EmptyNote>
              )}
            </div>
          </Panel>

          <Panel title="Jobseekers by State &amp; LGA">
            <div className="max-h-72 overflow-y-auto pr-1">
              {(stats?.jobseekersByState || []).map((row) => (
                <div key={row.state} className="border-b border-gray-50 last:border-0">
                  <button
                    onClick={() => toggleStateRow(row.state)}
                    className="w-full flex items-center justify-between py-2 text-sm hover:bg-gray-50 rounded-md px-1"
                  >
                    <span className="flex items-center gap-1.5 text-gray-700">
                      {expandedStateRows[row.state] ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                      {row.state}
                    </span>
                    <span className="font-semibold text-blue-600">{row.count}</span>
                  </button>
                  {expandedStateRows[row.state] && (
                    <div className="pl-6 pb-2 space-y-1">
                      {stateRowDetail[row.state]?.lgaBreakdown?.length ? (
                        stateRowDetail[row.state].lgaBreakdown.map((lga) => (
                          <div
                            key={lga.lga}
                            className="flex items-center justify-between text-xs text-gray-500"
                          >
                            <span>{lga.lga}</span>
                            <span>{lga.count}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-400 italic">Loading…</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* ── key insights ── */}
        <div>
          <h2 className="text-base font-semibold text-gray-800 mb-3">Key Insights</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {(stats?.insights || []).map((insight, i) => {
              const Icon = insightIcon[insight.type] || TrendingUp;
              return (
                <div
                  key={i}
                  className={`rounded-xl p-4 text-sm leading-relaxed flex gap-3 ${insightStyles[insight.type] || "bg-gray-50"}`}
                >
                  <Icon size={18} className="flex-shrink-0 mt-0.5" />
                  <span>{insight.text}</span>
                </div>
              );
            })}
            {!stats?.insights?.length && (
              <EmptyNote>Not enough data yet to generate insights.</EmptyNote>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  
  );
}